import { VariablesOf } from '@graphql-typed-document-node/core';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { EVENTS_PER_PAGE_HOME } from 'constants/events.constant';
import { datoRequester } from 'queries/requesters';
import { StaleTime } from 'queries/types';
import {
  MoreEventsDocument,
  PageEventDocument,
  PageEventsCardsDocument,
  PageEventsDocument,
} from 'types/api/dato-operations.types';

import { sortByStartDateTime } from 'components/@events/utils';

const EVENTS_PREFIX = 'events';

export const pageEventQuery = (
  variables: VariablesOf<typeof PageEventDocument>,
) =>
  queryOptions({
    queryKey: [EVENTS_PREFIX, 'detail', variables],
    queryFn: datoRequester(PageEventDocument, variables),
  });

export const pageEventsQuery = (
  variables: VariablesOf<typeof PageEventsDocument>,
) =>
  queryOptions({
    queryKey: [EVENTS_PREFIX, 'multiple', variables],
    queryFn: datoRequester(PageEventsDocument, variables),
  });

export const moreEventsQuery = (
  variables: VariablesOf<typeof MoreEventsDocument>,
) =>
  queryOptions({
    queryKey: [EVENTS_PREFIX, 'more-events', variables],
    queryFn: async (ctx) => {
      const { pageEvent } = await datoRequester(
        MoreEventsDocument,
        variables,
      )(ctx);

      const unsortedMoreEvents = pageEvent?.moreEvents.city.venues.flatMap(
        (venue) => venue.events,
      );

      const moreEvents = unsortedMoreEvents?.sort((a, b) =>
        sortByStartDateTime(a, b, 'asc'),
      );

      return { moreEvents };
    },
  });

/**
 * One month of the calendar. Keyed by its own window, so paging back and forth
 * between months never refetches.
 */
export const calendarMonthEventsQuery = (
  variables: VariablesOf<typeof PageEventsCardsDocument>,
) =>
  queryOptions({
    queryKey: [EVENTS_PREFIX, 'calendar-month', variables],
    queryFn: async (ctx) => {
      const { allPageEvents } = await datoRequester(
        PageEventsCardsDocument,
        variables,
      )(ctx);

      return { events: allPageEvents ?? [] };
    },
    staleTime: StaleTime.ONE_HOUR,
  });

export const infiniteEventsQuery = (
  variables: VariablesOf<typeof PageEventsCardsDocument>,
  eventsPerPage: number = EVENTS_PER_PAGE_HOME,
) => {
  //Removed endDatetime because date server/Azie was not same as on client -> Server/Client hydration mismatch due to different querykeys
  const { endDatetime, ...restFilters } = variables.filter || {};
  const restVariables = { ...variables, filter: restFilters };
  return infiniteQueryOptions({
    queryKey: [EVENTS_PREFIX, 'infinite', restVariables],
    queryFn: async (ctx) => {
      const { allPageEvents, meta } = await datoRequester(
        PageEventsCardsDocument,
        {
          ...variables,
          skip: ctx.pageParam,
        },
      )(ctx);

      const events = allPageEvents ?? [];

      return { events, meta };
    },

    staleTime: StaleTime.ONE_HOUR,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalLoadedCount = allPages.reduce(
        (acc, item) => acc + item.events.length,
        0,
      );

      const totalCount = lastPage.meta.count;

      return totalLoadedCount < totalCount
        ? allPages.length * eventsPerPage
        : undefined;
    },
  });
};
